import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit3, RefreshCw, AlertTriangle } from 'lucide-react';
import { text } from '../constants/configurationText';

// Renames a client. This cascades server-side to EVERY table that stores
// clientName — Employee, Department, MedicalInsuranceTier,
// ClientSplashScreen, ArchivedEmployee, DocumentRequest, OfficialHoliday,
// CompanyConfiguration (+ its archive), CompanyModulePermission,
// ClientCodePolicy, AdminLevel, WorkflowDefinition, ReportGenerationHistory,
// ScheduledReport, AttendanceDevice — all in a single transaction (see
// configurationService.renameClient). It is the ONLY way clientName can
// ever change once employees exist under a client; the employee edit forms
// (web + mobile) and every other write path have that field locked down
// specifically so this is the single place it happens.
const RenameCompanyModal = ({ theme, styles, t = text.en, currentName, onRename, onClose }) => {
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(false);

  const labelStyle = styles.modalLabel;

  const handleRename = async () => {
    setRenaming(true);
    const success = await onRename(currentName, newName);
    setRenaming(false);
    if (success) {
      setNewName('');
      onClose();
    }
  };

  return createPortal(
    <div style={styles.modal} onClick={onClose}>
      <div className="cfg-modal" style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{t.renameClient}</h3>
          <button style={styles.closeButton} onClick={onClose} aria-label={t.close}>
            <X size={18} />
          </button>
        </div>

        <div>
          <label style={labelStyle}>{t.currentName}</label>
          <input
            type="text"
            value={currentName || ''}
            disabled
            className="cfg-input"
            style={{ ...styles.input, marginBottom: '18px' }}
          />
        </div>

        <div>
          <label style={labelStyle}>{t.newName}</label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t.newNamePlaceholder}
            className="cfg-input"
            style={{ ...styles.input, marginBottom: 0 }}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !renaming && newName.trim()) handleRename();
            }}
          />
        </div>

        <div style={{ ...styles.notice('warning'), marginTop: '18px' }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '3px' }} />
          <p style={{ margin: 0 }}>
            {t.renameWarning}
          </p>
        </div>

        <div style={styles.modalButtons}>
          <button className="cfg-btn-secondary" style={styles.btnSecondary(renaming)} onClick={onClose} disabled={renaming}>
            {t.cancel}
          </button>
          <button
            className="cfg-btn-primary"
            style={styles.btnPrimary(renaming || !newName.trim())}
            onClick={handleRename}
            disabled={renaming || !newName.trim()}
          >
            {renaming ? (
              <>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                {t.renaming}
              </>
            ) : (
              <>
                <Edit3 size={16} />
                {t.renameClient}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RenameCompanyModal;
